import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { OfferingAgePricingRange } from '@/domain/offerings/offering'
import { Plus, Trash2, ClipboardPaste, AlertCircle } from 'lucide-react'
import { useState, useMemo } from 'react'
import { PriceLabelSimple } from '../shared/utils'

interface AgePricingRangeFormProps {
  ranges: OfferingAgePricingRange[]
  onChange: (ranges: OfferingAgePricingRange[]) => void
  readOnly?: boolean
  onValidationError?: (hasError: boolean) => void
}

export const AgePricingRangeForm = ({ ranges, onChange, readOnly = false, onValidationError }: AgePricingRangeFormProps) => {
  const [pasteValue, setPasteValue] = useState('')

  const handleAddRange = () => {
    onChange([...ranges, { rangeStart: 0, rangeEnd: 0, price: 0 }])
  }

  const handleRemoveRange = (index: number) => {
    onChange(ranges.filter((_, i) => i !== index))
  }

  const handleUpdateRange = (index: number, field: keyof OfferingAgePricingRange, value: number) => {
    const newRanges = [...ranges]
    newRanges[index] = { ...newRanges[index], [field]: value }
    onChange(newRanges)
  }

  const handlePaste = () => {
    if (!pasteValue.trim()) return

    const lines = pasteValue.split('\n')
    const newRanges: OfferingAgePricingRange[] = []

    lines.forEach(line => {
      const parts = line.split('\t').map(p => p.trim())
      if (parts.length >= 3) {
        const start = parseInt(parts[0].replace(/[^0-9]/g, ''))
        const end = parseInt(parts[1].replace(/[^0-9]/g, ''))
        
        let price: number
        const rawPrice = parts[2].replace(/[^0-9.,]/g, '')
        
        if (rawPrice.includes(',') && rawPrice.includes('.')) {
          const lastCommaIndex = rawPrice.lastIndexOf(',')
          const lastDotIndex = rawPrice.lastIndexOf('.')
          if (lastCommaIndex > lastDotIndex) {
            price = parseFloat(rawPrice.replace(/\./g, '').replace(',', '.'))
          } else {
            price = parseFloat(rawPrice.replace(/,/g, ''))
          }
        } else if (rawPrice.includes(',')) {
          price = parseFloat(rawPrice.replace(/,/g, ''))
        } else if (rawPrice.includes('.')) {
          price = parseFloat(rawPrice.replace(/\./g, ''))
        } else {
          price = parseFloat(rawPrice)
        }

        if (!isNaN(start) && !isNaN(end) && !isNaN(price)) {
          newRanges.push({ rangeStart: start, rangeEnd: end, price })
        }
      }
    })

    if (newRanges.length > 0) {
      onChange(newRanges)
      setPasteValue('')
    }
  }

  const validation = useMemo(() => {
    const errors: Record<number, string[]> = {}
    let hasAnyError = false

    ranges.forEach((range, index) => {
      const rowErrors: string[] = []
      
      // Rule: Start should not be larger than End
      if (range.rangeStart > range.rangeEnd && range.rangeEnd !== 0) {
        rowErrors.push('La edad inicial no puede ser mayor a la final')
        hasAnyError = true
      }

      // Rule: Check overlap with previous range
      if (index > 0) {
        const prevRange = ranges[index - 1]
        if (range.rangeStart <= prevRange.rangeEnd) {
          rowErrors.push(`Solapamiento con el rango anterior (${prevRange.rangeStart}-${prevRange.rangeEnd})`)
          hasAnyError = true
        }
      }

      if (rowErrors.length > 0) {
        errors[index] = rowErrors
      }
    })

    onValidationError?.(hasAnyError)
    return { errors, hasAnyError }
  }, [ranges, onValidationError])

  return (
    <div className="space-y-4">
      {!readOnly && (
        <div className="space-y-2 border rounded-md p-4 bg-muted/30">
          <Label className="text-xs font-bold uppercase text-muted-foreground">Pegar desde Excel/CSV (Inicio, Fin, Precio)</Label>
          <div className="flex gap-2">
            <Textarea
              placeholder="0	20	750.00&#10;21	29	800.00"
              value={pasteValue}
              onChange={(e) => setPasteValue(e.target.value)}
              className="min-h-[80px] text-xs font-mono"
            />
            <Button
              type="button"
              variant="outline"
              className="h-auto"
              onClick={handlePaste}
              disabled={!pasteValue.trim()}
            >
              <ClipboardPaste className="h-4 w-4 mr-2" />
              Procesar
            </Button>
          </div>
          <p className="text-[10px] text-muted-foreground">
            Copie las columnas de Edad Inicial, Edad Final y Precio de su planilla y péguelas aquí.
          </p>
        </div>
      )}

      <div className="border rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted text-muted-foreground text-xs uppercase">
            <tr>
              <th className="px-4 py-2 text-left font-medium">Edad Desde</th>
              <th className="px-4 py-2 text-left font-medium">Edad Hasta</th>
              <th className="px-4 py-2 text-left font-medium">Precio</th>
              {!readOnly && <th className="px-4 py-2 text-right font-medium">Acciones</th>}
            </tr>
          </thead>
          <tbody className="divide-y">
            {ranges.length === 0 ? (
              <tr>
                <td colSpan={readOnly ? 3 : 4} className="px-4 py-8 text-center text-muted-foreground italic">
                  No hay rangos definidos.
                </td>
              </tr>
            ) : (
              ranges.map((range, index) => (
                <tr key={index} className={validation.errors[index] ? 'bg-red-50/30' : ''}>
                  <td className="px-4 py-2">
                    {readOnly ? (
                      range.rangeStart
                    ) : (
                      <div className="space-y-1">
                        <Input
                          type="number"
                          value={range.rangeStart}
                          onChange={(e) => handleUpdateRange(index, 'rangeStart', parseInt(e.target.value) || 0)}
                          onFocus={(e) => e.target.select()}
                          className={`h-8 w-20 ${validation.errors[index]?.some(e => e.includes('inicial')) || validation.errors[index]?.some(e => e.includes('rango anterior')) ? 'border-red-500' : ''}`}
                        />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {readOnly ? (
                      range.rangeEnd
                    ) : (
                      <div className="space-y-1">
                        <Input
                          type="number"
                          value={range.rangeEnd}
                          onChange={(e) => handleUpdateRange(index, 'rangeEnd', parseInt(e.target.value) || 0)}
                          onFocus={(e) => e.target.select()}
                          className={`h-8 w-20 ${validation.errors[index]?.some(e => e.includes('final')) ? 'border-red-500' : ''}`}
                        />
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    {readOnly ? (
                      <PriceLabelSimple price={range.price} currency="ARS" />
                    ) : (
                      <div className="flex items-center gap-1">
                        <span className="text-muted-foreground">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={range.price || 0}
                          onChange={(e) => handleUpdateRange(index, 'price', parseFloat(e.target.value) || 0)}
                          onFocus={(e) => e.target.select()}
                          className="h-8 w-32"
                        />
                      </div>
                    )}
                  </td>
                  {!readOnly && (
                    <td className="px-4 py-2 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {validation.errors[index] && (
                          <div className="group relative">
                            <AlertCircle className="h-4 w-4 text-red-500 cursor-help" />
                            <div className="absolute bottom-full right-0 mb-2 w-64 hidden group-hover:block z-50">
                              <div className="bg-red-600 text-white text-[10px] rounded p-2 shadow-lg">
                                {validation.errors[index].map((err, i) => (
                                  <div key={i}>{err}</div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleRemoveRange(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!readOnly && (
        <Button type="button" variant="outline" size="sm" onClick={handleAddRange} className="w-full">
          <Plus className="h-4 w-4 mr-2" />
          Agregar Rango Manualmente
        </Button>
      )}
    </div>
  )
}
